import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../layout/MainLayout";
import Swal from "sweetalert2";
import { agentMe, listBills, listTunggakan } from "../lib/api";

function money(v) {
  return Number(v || 0).toFixed(2);
}

function formatDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB");
}

export default function AgentProfilePage() {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [bills, setBills] = useState([]);
  const [tunggakan, setTunggakan] = useState([]);

  const load = async () => {
    try {
      setLoading(true);

      const [meData, billsData, tunggakanData] = await Promise.all([
        agentMe(),
        listBills(),
        listTunggakan().catch(() => ({ items: [] })),
      ]);

      setMe(meData || null);
      setBills(Array.isArray(billsData) ? billsData : []);
      setTunggakan(Array.isArray(tunggakanData?.items) ? tunggakanData.items : []);
    } catch (e) {
      console.error("AgentProfilePage load error:", e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal load profil ejen.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    const normalized = bills.map((r) => {
      const noAkaun = r.no_akaun || r.noAkaun || "";
      const status = String(r.status || "").toUpperCase();
      const cajSewa = Number(r.caj_sewa ?? r.cajSewa ?? 0);
      const paidAmount = Number(r.paid_amount ?? r.paidAmount ?? 0);
      const kawasan = r.kawasan || "";
      return { noAkaun, status, cajSewa, paidAmount, kawasan };
    });

    const uniqueAccounts = new Set(
      normalized.map((x) => x.noAkaun).filter(Boolean)
    );

    const uniqueKawasans = new Set(
      normalized.map((x) => x.kawasan).filter(Boolean)
    );

    const paidRows = normalized.filter((x) => x.status === "PAID");
    const unpaidRows = normalized.filter((x) => x.status === "UNPAID");

    const recoveredAmount = paidRows.reduce(
      (sum, x) => sum + Number(x.paidAmount || x.cajSewa || 0),
      0
    );

    const totalBillAmount = normalized.reduce(
      (sum, x) => sum + Number(x.cajSewa || 0),
      0
    );

    const overdueAmount = tunggakan.reduce(
      (sum, x) => sum + Number(x.caj_sewa ?? x.cajSewa ?? 0),
      0
    );

    return {
      assignedUnits: uniqueAccounts.size,
      coveredKawasans: uniqueKawasans.size,
      totalBills: normalized.length,
      paidBills: paidRows.length,
      unpaidBills: unpaidRows.length,
      overdueBills: tunggakan.length,
      recoveredAmount,
      totalBillAmount,
      overdueAmount,
    };
  }, [bills, tunggakan]);

  const profile = useMemo(() => {
    if (!me) return null;

    return {
      id: me.id || me.agent_id || me.sub || "-",
      name: me.name || me.full_name || me.login_id || "Ejen",
      loginId: me.login_id || me.username || "-",
      phone: me.phone || me.telefon || "-",
      email: me.email || "-",
      agentType: me.agent_type || me.type || "-",
      joinedAt: me.created_at || me.createdAt || null,
    };
  }, [me]);

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto bg-white shadow-md rounded-lg p-6 mt-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Profil & KPI Ejen</h2>
            <p className="text-xs text-gray-500 mt-1">
              Ringkasan profil, liputan unit, kutipan, dan tunggakan semasa.
            </p>
          </div>

          <button
            onClick={load}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-gray-500">Memuatkan profil...</div>
        ) : !profile ? (
          <div className="py-10 text-center text-red-500">Gagal memuatkan profil ejen.</div>
        ) : (
          <>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1 border rounded-lg p-5 bg-gray-50">
                <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-3xl font-bold mx-auto">
                  {String(profile.name || "E").trim().charAt(0).toUpperCase()}
                </div>

                <div className="text-center mt-4">
                  <div className="text-xl font-bold text-gray-800">{profile.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{profile.agentType}</div>
                </div>

                <div className="mt-5 space-y-3 text-sm">
                  <div>
                    <div className="text-gray-500">ID Ejen</div>
                    <div className="font-semibold text-gray-800">{profile.id}</div>
                  </div>

                  <div>
                    <div className="text-gray-500">Login ID</div>
                    <div className="font-semibold text-gray-800">{profile.loginId}</div>
                  </div>

                  <div>
                    <div className="text-gray-500">Telefon</div>
                    <div className="font-semibold text-gray-800">{profile.phone}</div>
                  </div>

                  <div>
                    <div className="text-gray-500">Email</div>
                    <div className="font-semibold text-gray-800 break-all">{profile.email}</div>
                  </div>

                  <div>
                    <div className="text-gray-500">Tarikh Daftar</div>
                    <div className="font-semibold text-gray-800">{formatDate(profile.joinedAt)}</div>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-4 rounded border bg-blue-50">
                    <div className="text-xs text-gray-600">UNIT DITUGASKAN</div>
                    <div className="text-2xl font-bold text-blue-700 mt-1">
                      {stats.assignedUnits}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">unik no akaun</div>
                  </div>

                  <div className="p-4 rounded border bg-green-50">
                    <div className="text-xs text-gray-600">BIL BERJAYA DIKUTIP</div>
                    <div className="text-2xl font-bold text-green-700 mt-1">
                      {stats.paidBills}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">status PAID</div>
                  </div>

                  <div className="p-4 rounded border bg-red-50">
                    <div className="text-xs text-gray-600">TUNGGAKAN SEMASA</div>
                    <div className="text-2xl font-bold text-red-700 mt-1">
                      {stats.overdueBills}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">overdue unpaid</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div className="p-4 rounded border bg-emerald-50">
                    <div className="text-xs text-gray-600">JUMLAH KUTIPAN</div>
                    <div className="text-2xl font-bold text-emerald-700 mt-1">
                      RM {money(stats.recoveredAmount)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">paid amount / caj sewa</div>
                  </div>

                  <div className="p-4 rounded border bg-yellow-50">
                    <div className="text-xs text-gray-600">JUMLAH BIL DALAM PAPARAN</div>
                    <div className="text-2xl font-bold text-yellow-700 mt-1">
                      {stats.totalBills}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">paid + unpaid</div>
                  </div>

                  <div className="p-4 rounded border bg-purple-50">
                    <div className="text-xs text-gray-600">KAWASAN DILIPUTI</div>
                    <div className="text-2xl font-bold text-purple-700 mt-1">
                      {stats.coveredKawasans}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">berdasarkan bil</div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="text-sm font-semibold text-gray-800 mb-3">
                      Ringkasan Prestasi
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bil PAID</span>
                        <span className="font-semibold text-green-700">{stats.paidBills}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bil UNPAID</span>
                        <span className="font-semibold text-yellow-700">{stats.unpaidBills}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bil Tunggakan</span>
                        <span className="font-semibold text-red-700">{stats.overdueBills}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Jumlah Semua Bil</span>
                        <span className="font-semibold text-gray-800">
                          RM {money(stats.totalBillAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Jumlah Tunggakan</span>
                        <span className="font-semibold text-red-700">
                          RM {money(stats.overdueAmount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4 bg-white">
                    <div className="text-sm font-semibold text-gray-800 mb-3">
                      Nota KPI Demo
                    </div>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div>
                        <span className="font-semibold text-gray-800">Unit Ditugaskan:</span>{" "}
                        dikira daripada no akaun unik dalam bil ejen.
                      </div>
                      <div>
                        <span className="font-semibold text-gray-800">Bil Berjaya Dikutip:</span>{" "}
                        bil dengan status PAID.
                      </div>
                      <div>
                        <span className="font-semibold text-gray-800">Tunggakan:</span>{" "}
                        bil UNPAID yang melepasi due date.
                      </div>
                      <div>
                        KPI ini sesuai untuk demo pilot dan boleh diperhalusi kemudian.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}